/**
 * TabsDetailsView - Pure presentational tabs component for vessel details
 *
 * This component displays tab buttons without any context hooks.
 * It receives permissions and callbacks via props, making it usable in popups.
 */
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import React from 'react';
import {
  PiAnchorSimpleThin,
  PiBoatThin,
  PiHouseLineThin,
  PiListChecksThin,
  PiPersonThin,
} from 'react-icons/pi';
import { cn } from '@ais-ui/utils';
import { useLocalization } from '@ais-ui/locale-management';
import { TargetTab } from '../targetTypes';

const noop = () => {};

interface TabButtonProps {
  icon: React.ElementType;
  value: string;
  disabled: boolean;
  onClick: (value: any) => void;
  tooltipText: string;
}

const TabButton = ({
  icon: Icon,
  value,
  disabled,
  onClick,
  tooltipText,
}: TabButtonProps) => {
  return (
    <Tooltip>
      <TabsTrigger asChild value={value}>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className={cn(
              'justify-center size-8',
              disabled && 'opacity-50 cursor-default',
            )}
            onClick={disabled ? noop : () => onClick(value)}
          >
            <Icon
              className={cn(
                'size-4 flex-shrink-0',
                !disabled && 'group-hover/btn:text-primary',
              )}
            ></Icon>
          </Button>
        </TooltipTrigger>
      </TabsTrigger>
      <TooltipContent className="ml-4">{tooltipText}</TooltipContent>
    </Tooltip>
  );
};

export interface TabsDetailsViewProps {
  tab: TargetTab;
  onTabChange: (tab: TargetTab) => void;
  imo?: string;
  permissions: {
    mdmAdvancedVesselInfo?: boolean;
    mdmRegister?: boolean;
    mdmIntegrity?: boolean;
    nextport?: boolean;
  };
  /** Hide next port tab (e.g., in popups or detail view) */
  hideNextPort?: boolean;
}

export const TabsDetailsView = ({
  tab,
  onTabChange,
  imo,
  permissions,
  hideNextPort = false,
}: TabsDetailsViewProps) => {
  const { messages } = useLocalization();

  return (
    <Tabs value={tab} className="">
      <TabsList className="shadow-sm">
        <TabButton
          icon={PiHouseLineThin}
          value="base"
          disabled={false}
          onClick={onTabChange}
          tooltipText={messages.shipDetailsTabBaseTooltip}
        />
        <TabButton
          icon={PiBoatThin}
          value="advancedVessel"
          disabled={!permissions.mdmAdvancedVesselInfo}
          onClick={onTabChange}
          tooltipText={
            permissions.mdmAdvancedVesselInfo
              ? messages.shipDetailsTabAdvancedVesselInformationTooltip
              : messages.shipDetailsTabAdvancedVesselInformationTooltipDisable
          }
        />
        <TabButton
          icon={PiPersonThin}
          value="register"
          disabled={!permissions.mdmRegister}
          onClick={onTabChange}
          tooltipText={
            permissions.mdmRegister
              ? messages.shipDetailsTabRegisterTooltip
              : messages.shipDetailsTabRegisterTooltipDisable
          }
        />
        <TabButton
          icon={PiListChecksThin}
          value="integrity"
          disabled={!permissions.mdmIntegrity}
          onClick={onTabChange}
          tooltipText={
            permissions.mdmIntegrity
              ? messages.shipDetailsTabIntegrityTooltip
              : messages.shipDetailsTabIntegrityTooltipDisable
          }
        />
        {!hideNextPort && (
          <TabButton
            icon={PiAnchorSimpleThin}
            value="nextPort"
            disabled={!permissions.nextport || !imo}
            onClick={onTabChange}
            tooltipText={
              permissions.nextport
                ? imo
                  ? messages.shipDetailsTabNextPortTooltip
                  : messages.shipDetailsTabNextPortTooltipNoIMO
                : messages.shipDetailsTabNextPortTooltipDisable
            }
          />
        )}
      </TabsList>
    </Tabs>
  );
};
